import { MessageSquare } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
  createComment,
  getPurchaseOrderComments,
} from '@/modules/sales/api/comments.api'
import CommentCard from '@/modules/sales/components/leads/CommentCard'
import CommentInput from '@/modules/sales/components/leads/CommentInput'
import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toast } from '@/shared/lib/toast'
import socketService from '@/shared/socket/socket.service'

export default function PurchaseOrderCommentsModal({
  open,
  onOpenChange,
  purchaseOrder,
}) {
  const [comments, setComments] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const scrollContainerRef = useRef(null)
  const bottomRef = useRef(null)

  const poId = purchaseOrder?._id

  useEffect(() => {
    if (!open) return

    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    })
  }, [comments, open])

  // Fetch comments when opened
  useEffect(() => {
    if (!open || !poId) return

    let isMounted = true
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true)

    getPurchaseOrderComments(poId)
      .then((data) => {
        if (isMounted) {
          setComments(Array.isArray(data) ? data : [])
        }
      })
      .catch((error) => {
        console.error('Failed to load purchase order comments:', error)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [open, poId])

  const { notifications, markNotificationsAsRead, setReadCommentExpiry } =
    useCommentsStore()

  // Mark notifications as read
  useEffect(() => {
    if (!open || !poId) return

    const unreadNotificationIds = notifications
      .filter(
        (notification) =>
          (notification.purchaseOrderId === poId ||
            notification.purchaseOrder === poId) &&
          !notification.isRead,
      )
      .map((notification) => notification.id)

    if (!unreadNotificationIds.length) return

    const markAsRead = async () => {
      try {
        await markNotificationsAsRead(unreadNotificationIds)
        setReadCommentExpiry(unreadNotificationIds)
      } catch (error) {
        console.error('Failed to mark comment notifications as read:', error)
      }
    }

    markAsRead()
  }, [open, poId, notifications, markNotificationsAsRead, setReadCommentExpiry])

  // Socket subscription for real-time live comments
  useEffect(() => {
    if (!open || !poId) return

    const socket = socketService.connect()
    if (!socket) return undefined

    const joinRoom = () => {
      socket.emit('purchaseOrder:join', { purchaseOrderId: poId })
    }

    if (socket.connected) {
      joinRoom()
    } else {
      socket.once('connect', joinRoom)
    }

    const handleNewComment = (comment) => {
      if (comment.purchaseOrderId !== poId) return
      setComments((prev) => {
        if (
          prev.some(
            (c) =>
              (c.id && c.id === comment.id) || (c._id && c._id === comment._id),
          )
        ) {
          return prev
        }
        return [...prev, comment]
      })
    }

    const handleUpdatedComment = (comment) => {
      if (comment.purchaseOrderId !== poId) return
      setComments((prev) =>
        prev.map((c) =>
          (c.id && c.id === comment.id) || (c._id && c._id === comment._id)
            ? comment
            : c,
        ),
      )
    }

    const handleDeletedComment = ({ id }) => {
      setComments((prev) => prev.filter((c) => c.id !== id && c._id !== id))
    }

    socket.on('comment:new', handleNewComment)
    socket.on('comment:updated', handleUpdatedComment)
    socket.on('comment:deleted', handleDeletedComment)

    return () => {
      socket.off('connect', joinRoom)
      socket.off('comment:new', handleNewComment)
      socket.off('comment:updated', handleUpdatedComment)
      socket.off('comment:deleted', handleDeletedComment)

      if (socket.connected) {
        socket.emit('purchaseOrder:leave', { purchaseOrderId: poId })
      }

      setMessage('')
    }
  }, [open, poId])

  const handleSubmit = async () => {
    const value = message.trim()
    if (!value || isSubmitting || !poId) return

    setIsSubmitting(true)
    try {
      const newComment = await createComment({
        purchaseOrderId: poId,
        message: value,
      })
      if (newComment) {
        setComments((prev) => {
          if (
            prev.some(
              (c) =>
                (c.id && c.id === newComment.id) ||
                (c._id && c._id === newComment._id),
            )
          ) {
            return prev
          }
          return [...prev, newComment]
        })
      }
      setMessage('')
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.message || 'Failed to send comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const poNumber = purchaseOrder?.purchaseOrderNumber || 'Purchase Order'
  const vendorName =
    purchaseOrder?.vendor?.vendorName ||
    purchaseOrder?.vendor?.companyName ||
    ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-2xl flex-col p-0">
        <DialogHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  {poNumber}
                </DialogTitle>
                {vendorName && (
                  <p className="text-xs text-slate-500">
                    Vendor:{' '}
                    <span className="font-semibold text-slate-700">
                      {vendorName}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="mr-6 flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              <span className="size-2 animate-pulse rounded-full bg-emerald-500" />
              Purchase Order
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable comment thread */}
        <div
          ref={scrollContainerRef}
          className="flex-1 space-y-4 overflow-y-auto px-6 py-4"
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Loading comments...
            </div>
          ) : comments.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <MessageSquare className="size-6" />
              </div>
              <p className="text-sm font-semibold text-slate-700">
                No comments yet
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Send a message below to communicate directly between purchase
                and admin.
              </p>
            </div>
          ) : (
            comments.map((comment) => (
              <CommentCard key={comment.id || comment._id} comment={comment} />
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {/* Comment input box */}
        <CommentInput
          value={message}
          onChange={setMessage}
          onSubmit={handleSubmit}
          loading={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  )
}
